export const AUDIOBOOK_VOICES = [
  {
    id: "alloy",
    label: "Alloy",
    tone: "Balanced and articulate",
    bestFor: "Neutral narration and nonfiction clarity",
  },
  {
    id: "echo",
    label: "Echo",
    tone: "Cool and measured",
    bestFor: "Mystery, thrillers, and restrained tension",
  },
  {
    id: "fable",
    label: "Fable",
    tone: "Warm and expressive",
    bestFor: "Fantasy, YA, and character-led scenes",
  },
  {
    id: "onyx",
    label: "Onyx",
    tone: "Deep and grounded",
    bestFor: "High-stakes fiction and gravitas",
  },
  {
    id: "nova",
    label: "Nova",
    tone: "Bright and cinematic",
    bestFor: "Fast-moving scenes and broad appeal",
  },
  {
    id: "shimmer",
    label: "Shimmer",
    tone: "Soft and airy",
    bestFor: "Romance, introspection, and lyrical passages",
  },
] as const;

export const AUDIOBOOK_SPEED_OPTIONS = [
  {
    id: "measured",
    label: "Measured",
    wordsPerMinute: 135,
    rate: 0.93,
    direction: "Let descriptions breathe and land emotional beats cleanly.",
  },
  {
    id: "standard",
    label: "Standard",
    wordsPerMinute: 150,
    rate: 1,
    direction: "Prioritize clarity, flow, and everyday audiobook pacing.",
  },
  {
    id: "brisk",
    label: "Brisk",
    wordsPerMinute: 168,
    rate: 1.08,
    direction: "Keep momentum high without sounding rushed.",
  },
] as const;

export const AUDIOBOOK_EMOTION_OPTIONS = [
  { id: "subtle", label: "Subtle", direction: "Underplay emotion and stay restrained." },
  { id: "balanced", label: "Balanced", direction: "Keep emotion present but controlled." },
  { id: "cinematic", label: "Cinematic", direction: "Lean into mood, energy, and dramatic shifts." },
] as const;

export const AUDIOBOOK_AUDIO_FORMATS = [
  { id: "mp3", label: "MP3", contentType: "audio/mpeg" },
  { id: "wav", label: "WAV", contentType: "audio/wav" },
] as const;

export const DEFAULT_AUDIOBOOK_VOICE = AUDIOBOOK_VOICES[0].id;
export const DEFAULT_AUDIOBOOK_SPEED = AUDIOBOOK_SPEED_OPTIONS[1].id;
export const DEFAULT_AUDIOBOOK_EMOTION = AUDIOBOOK_EMOTION_OPTIONS[1].id;
export const DEFAULT_AUDIOBOOK_FORMAT = AUDIOBOOK_AUDIO_FORMATS[0].id;

export type AudiobookVoiceId = (typeof AUDIOBOOK_VOICES)[number]["id"];
export type AudiobookSpeedId = (typeof AUDIOBOOK_SPEED_OPTIONS)[number]["id"];
export type AudiobookEmotionId = (typeof AUDIOBOOK_EMOTION_OPTIONS)[number]["id"];
export type AudiobookAudioFormat = (typeof AUDIOBOOK_AUDIO_FORMATS)[number]["id"];

const audiobookVoiceIds = new Set<string>(AUDIOBOOK_VOICES.map((voice) => voice.id));
const audiobookSpeedIds = new Set<string>(AUDIOBOOK_SPEED_OPTIONS.map((speed) => speed.id));
const audiobookEmotionIds = new Set<string>(AUDIOBOOK_EMOTION_OPTIONS.map((emotion) => emotion.id));
const audiobookFormatIds = new Set<string>(AUDIOBOOK_AUDIO_FORMATS.map((format) => format.id));

export const normalizeAudiobookVoiceId = (value?: string | null): AudiobookVoiceId =>
  audiobookVoiceIds.has(value ?? "") ? (value as AudiobookVoiceId) : DEFAULT_AUDIOBOOK_VOICE;

export const normalizeAudiobookSpeedId = (value?: string | null): AudiobookSpeedId =>
  audiobookSpeedIds.has(value ?? "") ? (value as AudiobookSpeedId) : DEFAULT_AUDIOBOOK_SPEED;

export const normalizeAudiobookEmotionId = (value?: string | null): AudiobookEmotionId =>
  audiobookEmotionIds.has(value ?? "") ? (value as AudiobookEmotionId) : DEFAULT_AUDIOBOOK_EMOTION;

export const normalizeAudiobookFormat = (value?: string | null): AudiobookAudioFormat =>
  audiobookFormatIds.has(value ?? "") ? (value as AudiobookAudioFormat) : DEFAULT_AUDIOBOOK_FORMAT;

export const getAudiobookVoice = (voiceId?: string | null) =>
  AUDIOBOOK_VOICES.find((voice) => voice.id === voiceId) ?? AUDIOBOOK_VOICES[0];

export const getAudiobookSpeed = (speedId?: string | null) =>
  AUDIOBOOK_SPEED_OPTIONS.find((speed) => speed.id === speedId) ?? AUDIOBOOK_SPEED_OPTIONS[1];

export const getAudiobookEmotion = (emotionId?: string | null) =>
  AUDIOBOOK_EMOTION_OPTIONS.find((emotion) => emotion.id === emotionId) ?? AUDIOBOOK_EMOTION_OPTIONS[1];

export const getAudiobookFormat = (formatId?: string | null) =>
  AUDIOBOOK_AUDIO_FORMATS.find((format) => format.id === formatId) ?? AUDIOBOOK_AUDIO_FORMATS[0];

export const estimateNarrationSeconds = (wordCount: number, speedId?: string | null) => {
  const wordsPerMinute = getAudiobookSpeed(speedId).wordsPerMinute;
  if (!wordCount || wordCount <= 0) return 0;
  return Math.max(Math.round((wordCount / wordsPerMinute) * 60), 1);
};

export const formatDurationLabel = (seconds: number) => {
  const safeSeconds = Math.max(Math.round(seconds), 0);
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;
  if (minutes <= 0) return `${remainingSeconds}s`;
  return `${minutes}m ${remainingSeconds.toString().padStart(2, "0")}s`;
};
